import classNames from 'classnames';
import { useEffect, useMemo, useState } from 'react';
import { DeepPartial, FieldPath, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { BiPencil } from 'react-icons/bi';
import { FaCheck, FaTimes } from 'react-icons/fa';
import { useParams } from 'react-router-dom';
import { InferType, object, ObjectSchema, string } from 'yup';

import { PermissionKeys, SetAgreementModel, useGetAgreementsByIdQuery, usePutAgreementsByIdMutation } from '@client/api';
import {
  FormCancelButton,
  FormField,
  FormSubmitButton,
  Input,
  isDefinedString,
  Skeleton,
  Typography,
} from '@client/toolkit';
import { yupResolver } from '@hookform/resolvers/yup';

import { CancelRequestModal } from '../../../../../components';
import { useAgreementPermissions } from '../../../../../hooks/useAgreementPermissions/useAgreementPermissions';

type FormModel = Pick<SetAgreementModel, 'name'>;

const schema: ObjectSchema<FormModel> = object({
  name: string().trim().required(),
});

type FormValues = InferType<typeof schema>;
type FormPath = FieldPath<FormModel>;
type FormDefaultValues = DeepPartial<FormValues>;

export interface AgreementNameFieldProps {
  className?: string;
}

export function AgreementNameField({ className }: AgreementNameFieldProps) {
  const { t: tCommon } = useTranslation();
  const { t: tGlobal } = useTranslation('global');

  const { id } = useParams<{ id: string }>();
  const { data: agreement, isFetching } = useGetAgreementsByIdQuery({ id: id ?? '' }, { skip: id == null });
  const { hasPermission } = useAgreementPermissions({ id: id ?? '' });

  const canEdit = hasPermission([PermissionKeys.Agreement.Update]);

  const [putAgreement] = usePutAgreementsByIdMutation();

  const buildingName = useMemo(
    () => agreement?.spaces?.[0]?.space?.building?.name,
    [agreement?.spaces],
  );

  const displayName = useMemo(() => {
    if (!isDefinedString(agreement?.name)) return undefined;
    return isDefinedString(buildingName) ? `${buildingName} – ${agreement?.name}` : agreement?.name;
  }, [agreement?.name, buildingName]);

  const [editing, setEditing] = useState<boolean>(false);

  const onSubmit = async (data: FormValues) => {
    if (id == null || agreement == null) return;

    try {
      await putAgreement({
        id: agreement.id,
        body: {
          ...agreement,
          ...data,
        },
      }).unwrap();
    } catch (e) {
      return;
    }

    setEditing(false);
  };

  const defaultValues: FormDefaultValues = useMemo(
    () => ({
      name: isDefinedString(agreement?.name) ? agreement?.name : undefined,
    }),
    [agreement?.name],
  );

  const methods = useForm<FormValues>({
    defaultValues,
    mode: 'onTouched',
    resolver: yupResolver(schema),
  });

  const {
    handleSubmit,
    formState: { isDirty },
    reset,
  } = methods;

  const [cancelingRequest, setCancelingRequest] = useState<boolean>(false);

  const checkCancelRequest = () => {
    if (isDirty) {
      setCancelingRequest(true);
    } else {
      setEditing(false);
    }
  };

  const handleConfirmCancelRequest = () => {
    setEditing(false);
    setCancelingRequest(false);
  };

  const handleCancelCancelRequest = () => {
    setCancelingRequest(false);
  };

  const handleOpenEdit = () => {
    setEditing(true);
  };

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, editing]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <FormProvider {...methods}>
        <form noValidate className={className} onSubmit={handleSubmit(onSubmit)}>
          {!editing ? (
            isFetching ? (
              <Skeleton variant="text" className="w-[40%] mt-3" />
            ) : (
              <div className="flex items-center mt-3 mb-1 mr-9">
                <div className={classNames('relative', canEdit && 'cursor-pointer group')} onClick={canEdit ? handleOpenEdit : undefined}>
                  <Typography
                    variant="section"
                    className={classNames(
                      canEdit && 'group-hover:underline',
                      isDefinedString(agreement?.name) ? 'text-gray-900' : 'text-gray-400',
                    )}
                  >
                    {isDefinedString(displayName)
                      ? displayName
                      : tCommon('untitledEntity', { entity: tGlobal('entity.agreement') })}
                  </Typography>
                  {canEdit && (
                    <div className="group-hover:opacity-100 group-hover:visible invisible opacity-0 text-lg rounded-full p-1 absolute -right-7 -top-0.5 bottom-0 pointer-events-none transition-opacity ease-in-out duration-75 text-primary">
                      <BiPencil />
                    </div>
                  )}
                </div>
              </div>
            )
          ) : (
            <div className="flex items-center group cursor-pointer relative mt-1 mr-8">
              <div className="flex-1">
                <FormField<FormValues, FormPath>
                  name="name"
                  defaultValue={''}
                  render={({ field }) => <Input {...field} data-1p-ignore />}
                  HelperTextProps={{
                    className: 'absolute -bottom-4 left-0',
                  }}
                />
              </div>
              <div className="space-x-1 ml-2 whitespace-nowrap">
                <FormSubmitButton
                  color="primary"
                  disabledVariant="dark"
                  type="submit"
                  size="sm"
                  disabled={isFetching}
                >
                  <FaCheck className="text-black" />
                </FormSubmitButton>
                <FormCancelButton color="primary-dark" disabledVariant="dark" size="sm" onClick={checkCancelRequest}>
                  <FaTimes />
                </FormCancelButton>
              </div>
            </div>
          )}
        </form>
      </FormProvider>

      <CancelRequestModal
        open={cancelingRequest}
        onConfirm={handleConfirmCancelRequest}
        onCancel={handleCancelCancelRequest}
      />
    </>
  );
}
